import { Component, Fragment, Host, Prop, h } from '@stencil/core';

import { clearCaptcha, onQuestionAnswered, onStateFieldAnswered } from '../../../actions/actions.answers';
import { onContactFormInit } from '../../../actions/actions.validation.customer';
import { HierarchicalData, ReplacementVehicleOptions, RvLookingForData } from '../../../interfaces/interface.lookup';
import { onEngagementInit as onEngagementInit } from '../../../tracking/tracking.asc';
import { ENGAGEMENT_FORMS } from '../../../utils/utils.asc-analytics';
import { QUESTION_ADDITIONAL_COMMENTS } from '../../../utils/utils.question';
import { isLucidTheme } from '../../../utils/utils.theme';

@Component({
  tag: 'customer-contact',
  styleUrl: 'customer-contact.css',
})
export class CustomerContact {
  @Prop() showFormValidation: boolean = false;
  @Prop() formErrors: Set<string>;
  @Prop() fieldErrors: Set<string>;
  @Prop() contactFirstName: string;
  @Prop() contactLastName: string;
  @Prop() contactEmail: string;
  @Prop() contactPhone: string;
  @Prop() contactZip: string;
  @Prop() contactAdditionalComment: string;
  @Prop() dealerName: string;
  @Prop() contactLegalAcceptance: boolean;
  @Prop() replacementIsLookingForReplacementVehicle: boolean;
  @Prop() replacementIsLookingForNewCars: boolean;
  @Prop() replacementIsLookingForCertifiedCars: boolean;
  @Prop() replacementIsLookingForUsedCars: boolean;
  @Prop() replacementMinYear: number;
  @Prop() replacementMaxYear: number;
  @Prop() replacementMakeModelId: string;
  @Prop() replacementMakeId: string;
  @Prop() replacementYearRangeMin: number;
  @Prop() replacementYearRangeMax: number;
  @Prop() replacementMakesModels: HierarchicalData[];
  @Prop() rvLookingForOptions: RvLookingForData[];
  @Prop() replacementVehicleOptions?: ReplacementVehicleOptions;
  @Prop() showReplacementVehicleSection: boolean = true;
  @Prop() answerValueForLeadTypeNo: string;
  @Prop() theme: string;
  @Prop() isKelleyBuysYourCarRequested: boolean = false;
  @Prop() isKelleyBuysYourCarFakeDoor: boolean = false;
  @Prop() isInstantCashOfferRequested: boolean = false;
  @Prop() showOnlyConsent: boolean = false;
  @Prop() showLegalAcceptance: boolean = true;
  @Prop() showLegalText: boolean = true;

  @Prop() showAdditionalComment: boolean = false;
  @Prop() isConsumerOffer: boolean = true;
  @Prop() overrideHeaderText: string;
  @Prop() overrideSubHeaderText: string;
  componentWillLoad() {
    onContactFormInit();
    clearCaptcha();

    onStateFieldAnswered('disposalOptions', 'isKelleyBuysYourCar', this.isKelleyBuysYourCarRequested);
    onStateFieldAnswered('disposalOptions', 'isInstantCashOffer', this.isInstantCashOfferRequested);
    onStateFieldAnswered('disposalOptions', 'isKelleyBuysYourCarFakeDoor', this.isKelleyBuysYourCarFakeDoor);
  }

  componentDidLoad() {
    onEngagementInit(ENGAGEMENT_FORMS.customerContact);
  }

  onContactComment(value: string) {
    onQuestionAnswered(QUESTION_ADDITIONAL_COMMENTS, value, '');
  }

  renderContactSubheader() {
    if (isLucidTheme(this.theme)) {
      return '';
    }
    return this.dealerName ? `${this.dealerName} may contact you about your car.` : this.overrideSubHeaderText || `Up to 3 Participating Dealers may contact you about your car.`;
  }

  render() {
    return (
      <Host data-testId="customerContact">
        <div class="box-shadow">
          {this.isConsumerOffer && (
            <>
              <div class="text-lg font-bold mt-5">{this.overrideHeaderText || 'Where Should We Send Your Offer?'}</div>
              <div class="mb-4">
                {this.renderContactSubheader()} See
                <ico-hyperlink class="inline-block mx-1" noUnderline href={process.env.PRIVACY_POLICY_URL} target="_blank">
                  Privacy Statement
                </ico-hyperlink>
                for more.
              </div>
              <customer-info
                id="customerInfo"
                showFormValidation={this.showFormValidation}
                fieldErrors={this.fieldErrors}
                firstName={this.contactFirstName}
                lastName={this.contactLastName}
                email={this.contactEmail}
                phone={this.contactPhone}
                zip={this.contactZip}
                legalAcceptance={this.contactLegalAcceptance}
                showOnlyConsent={this.showOnlyConsent}
                showLegalAcceptance={this.showLegalAcceptance}
              />

              {!this.showOnlyConsent && (
                <contact-replacement-vehicle
                  id="replacementVehicle"
                  class="pb-4"
                  showFormValidation={this.showFormValidation}
                  formErrors={this.formErrors}
                  isLookingForReplacementVehicle={this.replacementIsLookingForReplacementVehicle}
                  isLookingForNewCars={this.replacementIsLookingForNewCars}
                  isLookingForCertifiedCars={this.replacementIsLookingForCertifiedCars}
                  isLookingForUsedCars={this.replacementIsLookingForUsedCars}
                  minYear={this.replacementMinYear}
                  maxYear={this.replacementMaxYear}
                  makeModelId={this.replacementMakeModelId}
                  makeId={this.replacementMakeId}
                  yearRangeMin={this.replacementYearRangeMin}
                  yearRangeMax={this.replacementYearRangeMax}
                  makesModels={this.replacementMakesModels}
                  rvLookingForOptions={this.rvLookingForOptions}
                  replacementVehicleOptions={this.replacementVehicleOptions}
                  showReplacementVehicleSection={this.showReplacementVehicleSection}
                  answerValueForLeadTypeNo={this.answerValueForLeadTypeNo}
                />
              )}
            </>
          )}
          {this.showAdditionalComment && (
            <div>
              <div class="text-lg font-bold mt-5">Additional Comment</div>
              <div class="text-sm mb-4">Please give us any addition information that might help us better evaluate this vehicle.</div>
              <div class="input-multiline__message">max 1000 characters</div>
              <input-multiline
                label="Comment"
                maxLength={1000}
                rowCount={5}
                value={this.contactAdditionalComment}
                whenChanged={this.onContactComment}
                data-testId={`input-${QUESTION_ADDITIONAL_COMMENTS}`}
              />
            </div>
          )}
          <div class="pb-4">
            <slot name="captcha" />
            {this.showFormValidation && this.formErrors.has('qint/CaptchaResult') && (
              <div class="text-center">
                <span class="text-xs pl-4 text-clrs-argo-red">Please verify that you are not a robot.</span>
              </div>
            )}
          </div>
          {this.showLegalText && (
            <offer-certificate-disclaimer
              is-instant-cash-offer-requested={this.isInstantCashOfferRequested}
              is-kelley-buys-your-car-requested={this.isKelleyBuysYourCarRequested}
            />
          )}
        </div>
      </Host>
    );
  }
}
